import React, { type FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import ReactGA from 'react-ga4';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';

import Dropdown from '../Dropdown/Dropdown';
import Button from '../Button/Button';

import styles from './Filter.module.scss';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

type FilterOption =
  | {
      label: string;
      value: string;
    }
  | string;

type Props = {
  name: string;
  value: string;
  defaultLabel: string;
  options: FilterOption[];
  forceDropdown?: boolean;
  setValue: (value: string) => void;
};

const Filter: FC<Props> = ({ name, value, defaultLabel, options, setValue, forceDropdown = false }) => {
  const { t } = useTranslation('common');
  const breakpoint: Breakpoint = useBreakpoint();
  const { isGoogleAnalytics } = useAppConfig();
  const user = useAccountStore((state) => state.user);
  const isLoggedIn = !!user;
  const userId = isLoggedIn ? user.id : 'Guest user';
  const deviceType = useGADeviceType();

  if (!options.length) {
    return null;
  }
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    const selectedOption = options.find((opt) => (typeof opt === 'string' ? opt === selectedValue : opt.value === selectedValue));
    const label = typeof selectedOption === 'string' ? selectedOption : selectedOption?.label;
    if (isGoogleAnalytics) {
      sendGAEvent('category_filter_applied', {
        filter_name: name,
        filter_value: label,
        userid_no: userId,
        device_type: deviceType,
      });
    }
    setValue(event.target.value);
  };

  const showFilterRow = !forceDropdown && breakpoint >= Breakpoint.md && options.length < 6;

  const buttonFilter = (optionValue: any) => {
    const selectedOption = options.find((opt) => (typeof opt === 'string' ? opt === optionValue : opt.value === optionValue));
    const label = typeof selectedOption === 'string' ? selectedOption : selectedOption?.label;
    if (isGoogleAnalytics) {
      sendGAEvent('category_filter_applied', {
        filter_name: name,
        filter_value: label,
        userid_no: userId,
        device_type: deviceType,
      });
    }
    setValue(optionValue);
  };

  return (
    <Fragment>
      {showFilterRow ? (
        <div className={styles.filterRow} role="listbox" aria-label={t('filter_videos_by', { name })}>
          {options.map((option) => {
            const optionLabel = typeof option === 'string' ? option : option.label;
            const optionValue = typeof option === 'string' ? option : option.value;
            const active = value === optionValue;
            // type Variant = 'contained' | 'outlined' | 'text' | 'danger' | 'delete';
            return (
              <Button
                label={optionLabel}
                color="secondary"
                onClick={() => buttonFilter(optionValue)}
                key={optionValue}
                active={active}
                role="option"
                variant="contained"
                aria-selected={active}
              />
            );
          })}
          {name !== 'season' && (
            <Button
              variant="contained"
              color="secondary"
              label={defaultLabel}
              onClick={() => setValue('')}
              active={value === ''}
              key={defaultLabel}
              role="option"
              aria-selected={value === ''}
            />
          )}
        </div>
      ) : (
        <div className={styles.filterDropDown}>
          <Dropdown
            className={styles.dropDown}
            size="small"
            options={options}
            defaultLabel={defaultLabel}
            name={name}
            value={value}
            onChange={handleChange}
            aria-label={t('filter_videos_by', { name })}
            hideOptional
          />
        </div>
      )}
    </Fragment>
  );
};

export default Filter;
